import { computed } from 'vue';
import { useUISettings } from './useUISettings';
import { useAccount } from './useAccount';

/**
 * Select translation based on locale priority.
 * @param {Object} translations - Translations object with locale keys
 * @param {string} agentLocale - Agent's preferred locale
 * @param {string} accountLocale - Account's default locale
 * @returns {string|null} Selected translation or null
 */
export function selectTranslation(translations, agentLocale, accountLocale) {
  if (!translations || Object.keys(translations).length === 0) return null;

  if (agentLocale && translations[agentLocale]) {
    return translations[agentLocale];
  }
  if (accountLocale && translations[accountLocale]) {
    return translations[accountLocale];
  }
  return translations[Object.keys(translations)[0]];
}

/**
 * Composable to extract translation state/content from contentAttributes.
 * @param {Ref|Reactive} contentAttributes - Ref or reactive object containing `translations` property
 * @returns {Object} { hasTranslations, translationContent, autoTranslated, sourceLanguage, targetLanguage }
 */
export function useTranslations(contentAttributes) {
  const { uiSettings } = useUISettings();
  const { currentAccount } = useAccount();

  const hasTranslations = computed(() => {
    if (!contentAttributes.value) return false;
    const { translations = {} } = contentAttributes.value;
    return Object.keys(translations || {}).length > 0;
  });

  const translationContent = computed(() => {
    if (!hasTranslations.value) return null;
    return selectTranslation(
      contentAttributes.value.translations,
      uiSettings.value?.locale,
      currentAccount.value?.locale
    );
  });

  // Set when this message was translated on its way out to the customer. The
  // API may camelCase content_attributes, so both spellings are accepted.
  const autoTranslated = computed(() => {
    const attributes = contentAttributes.value || {};
    return attributes.autoTranslated || attributes.auto_translated || null;
  });

  // For an outgoing translated message the stored `content` is what the
  // customer actually received, and `translations` holds the agent's original —
  // the reverse of an incoming message. Callers need to know which way round
  // they are, or they show the agent their own words and hide what was sent.
  const isOutgoingTranslation = computed(() => Boolean(autoTranslated.value));

  // Recorded when translation applied to this reply but the agent sent their
  // own language anyway, so the bubble can say so rather than look untouched.
  const translationSkipped = computed(() => {
    const attributes = contentAttributes.value || {};
    return attributes.translationSkipped || attributes.translation_skipped || null;
  });

  const sourceLanguage = computed(() => autoTranslated.value?.from || null);
  const targetLanguage = computed(() => autoTranslated.value?.to || null);

  return {
    hasTranslations,
    translationContent,
    autoTranslated,
    isOutgoingTranslation,
    translationSkipped,
    sourceLanguage,
    targetLanguage,
  };
}
